// Per-row UI for a material inside the duplicate-finder panel. Exposes
// Edit, Deactivate, Keep (radio within group), and Merge into keep.
import { useState } from "react";
import type { MfgMaterial } from "@/lib/manufacturing/types";

type Props = {
  material: MfgMaterial;
  busy: boolean;
  isKeeper?: boolean;
  canMerge?: boolean;
  mergeEnabled?: boolean;
  onEdit: (m: MfgMaterial) => void;
  onDeactivate: (m: MfgMaterial) => Promise<{ ok: true } | { ok: false; error: string }>;
  onMarkKeep?: () => void;
  onRequestMerge?: () => void;
};

const editBtnClass =
  "rounded-md border border-gray-300 bg-white px-2 py-0.5 text-[11px] text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-200 dark:hover:bg-zinc-700";

const dangerBtnClass =
  "rounded-md border border-red-300 bg-white px-2 py-0.5 text-[11px] text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-red-700 dark:bg-zinc-800 dark:text-red-300 dark:hover:bg-red-950/40";

const keepBtnClass =
  "rounded-md border border-emerald-300 bg-white px-2 py-0.5 text-[11px] font-medium text-emerald-800 hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-emerald-800 dark:bg-zinc-900 dark:text-emerald-200";

const keepActiveClass =
  "rounded-md border border-emerald-600 bg-emerald-600 px-2 py-0.5 text-[11px] font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60";

const mergeBtnClass =
  "rounded-md border border-sky-300 bg-white px-2 py-0.5 text-[11px] font-medium text-sky-900 hover:bg-sky-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-sky-800 dark:bg-zinc-900 dark:text-sky-200";

export function ManufacturingMaterialDuplicateRow({
  material,
  busy,
  isKeeper = false,
  canMerge = false,
  mergeEnabled = false,
  onEdit,
  onDeactivate,
  onMarkKeep,
  onRequestMerge,
}: Props) {
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleDeactivate() {
    if (working || !material.is_active) return;
    if (!window.confirm(`Deactivate "${material.name}"? It will be hidden from active lists.`)) return;
    setWorking(true);
    setError(null);
    const r = await onDeactivate(material);
    setWorking(false);
    if (!r.ok) setError(r.error);
  }

  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
      <div className="min-w-0">
        <div className="text-xs text-gray-900 dark:text-zinc-100">
          {material.code} · {material.unit} · {material.category || "Uncategorized"}
          {isKeeper ? (
            <span className="ml-1.5 rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200">
              Keep
            </span>
          ) : null}
        </div>
        <div className="text-[11px] text-gray-600 dark:text-zinc-400">
          On hand: {Number(material.on_hand_qty).toFixed(1)} · WAC: $
          {Number(material.unit_cost).toFixed(2)}
          {material.is_active ? "" : " · inactive"}
        </div>
        {error ? (
          <div className="mt-0.5 text-[10px] text-red-700 dark:text-red-300">{error}</div>
        ) : null}
      </div>
      <div className="flex flex-wrap shrink-0 items-center gap-1.5">
        {canMerge && onMarkKeep ? (
          <button
            type="button"
            className={isKeeper ? keepActiveClass : keepBtnClass}
            onClick={onMarkKeep}
            disabled={busy}
            title="Keep this material; merge siblings into it"
          >
            {isKeeper ? "Keeping" : "Keep"}
          </button>
        ) : null}
        {canMerge && onRequestMerge ? (
          <button
            type="button"
            className={mergeBtnClass}
            onClick={onRequestMerge}
            disabled={busy || !mergeEnabled}
            title={
              mergeEnabled
                ? "Transfer dependents + inventory into Keep, then permanently delete this row"
                : "Select Keep on another material in this group first"
            }
          >
            Merge into keep
          </button>
        ) : null}
        <button
          type="button"
          className={editBtnClass}
          onClick={() => onEdit(material)}
          disabled={busy}
        >
          Edit
        </button>
        <button
          type="button"
          className={dangerBtnClass}
          onClick={() => void handleDeactivate()}
          disabled={busy || working || !material.is_active}
          title={
            material.is_active
              ? "Mark this material inactive (soft delete)"
              : "Already inactive"
          }
        >
          {working ? "Deactivating…" : material.is_active ? "Deactivate" : "Inactive"}
        </button>
      </div>
    </div>
  );
}
